import { useModal } from "@ebay/nice-modal-react"
import { RefreshCw } from "lucide-react"
import { observer } from "mobx-react"
import { useCallback, useEffect, useState } from "react"
import { browserCookieImportMessage } from "../../browser/cookieImport"
import { embeddedBrowserStore } from "../../browser/store"
import { embeddedBrowserSurfaceStore } from "../../browser/surfaceStore"
import {
    getEmbeddedBrowserStatus,
    importEmbeddedBrowserCookies,
    listEmbeddedBrowserCookieProfiles,
    type EmbeddedBrowserCookieProfile,
} from "../../electronAPIs/browser"
import { ModalConfirm } from "../ui"

type BusyOperation = "import" | "reset" | "delete" | "profiles" | null

export const BrowserSettingsTab = observer(function BrowserSettingsTab(): JSX.Element {
    const confirm = useModal(ModalConfirm)
    const running = embeddedBrowserSurfaceStore.isRunning
    const [busy, setBusy] = useState<BusyOperation>("profiles")
    const [message, setMessage] = useState<string | null>(null)
    const [profiles, setProfiles] = useState<EmbeddedBrowserCookieProfile[] | null>(null)
    const [selectedProfileId, setSelectedProfileId] = useState("")
    const [cookieImportSupported, setCookieImportSupported] = useState<boolean | null>(null)

    const loadProfiles = useCallback(async () => {
        setBusy("profiles")
        setMessage(null)
        const status = await getEmbeddedBrowserStatus()
        const supported = status?.features.cookieImport === true
        setCookieImportSupported(supported)
        if (!supported) {
            setProfiles([])
            setBusy(null)
            return
        }
        const availableProfiles = await listEmbeddedBrowserCookieProfiles()
        setProfiles(availableProfiles)
        setSelectedProfileId((current) => (availableProfiles?.some((profile) => profile.id === current) ? current : (availableProfiles?.[0]?.id ?? "")))
        setBusy(null)
        if (availableProfiles === null) setMessage("Browser profiles could not be read on this device.")
    }, [])

    useEffect(() => {
        void loadProfiles()
    }, [loadProfiles])

    const importCookies = () => {
        const profile = profiles?.find((candidate) => candidate.id === selectedProfileId)
        if (!profile) return
        confirm.show({
            title: `Replace Browser profile with sessions from ${profile.name}?`,
            description:
                "WildLark replaces its current isolated Browser profile with a fresh profile containing these cookies. Any active Browser session stops, and existing WildLark Browser sign-ins and site data are deleted. The source browser is unchanged. Passwords, history, bookmarks, autofill, and raw cookie values never enter the renderer or agent conversation.",
            buttonText: "Replace and import",
            onConfirm: () => {
                setBusy("import")
                setMessage(null)
                void importEmbeddedBrowserCookies(profile.id).then((result) => {
                    setBusy(null)
                    setMessage(browserCookieImportMessage(result))
                })
            },
        })
    }

    const resetTabs = () => {
        if (!running) return
        confirm.show({
            title: "Reset open Browser tabs?",
            description:
                "This closes every embedded Browser tab, clears transient refs, screenshots, and navigation state, and pauses agent control. Website sign-ins and site data remain.",
            buttonText: "Reset tabs",
            onConfirm: () => {
                setBusy("reset")
                embeddedBrowserStore.reset()
                embeddedBrowserStore.setNotice("Open Browser tabs were reset. Website sign-ins were kept; agent control is paused.")
                setBusy(null)
                setMessage("Open Browser tabs were reset. Website sign-ins were kept; agent control is paused.")
            },
        })
    }

    const deleteData = () => {
        confirm.show({
            title: "Delete embedded Browser data?",
            description:
                "This signs you out of websites in WildLark Browser and deletes its cookies, cache, local storage, IndexedDB, and service workers on this device. It does not delete WildLark tasks or data from Chrome, Edge, or your system browser.",
            buttonText: "Delete browser data",
            onConfirm: () => {
                setBusy("delete")
                setMessage(null)
                void embeddedBrowserStore
                    .clearData()
                    .then(() => setMessage("Embedded Browser data was deleted."))
                    .catch(() => setMessage("Embedded Browser data could not be deleted. Please try again."))
                    .finally(() => setBusy(null))
            },
        })
    }

    return (
        <div className="flex flex-col gap-7">
            <div>
                <h3 className="text-base font-semibold text-base-content">Browser</h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted">
                    Agents ask before starting WildLark Browser. It runs hidden in an isolated profile, and only appears in the task sidebar while a session is
                    running so you can watch or take over.
                </p>
            </div>

            <section className="border border-border bg-base-200/40 p-3">
                <h4 className="text-sm font-medium text-base-content">Agent-first Browser</h4>
                <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted">
                    The Browser tool suite remains discoverable to agents, but page access is blocked until an agent calls wildlark_browser_start and you allow
                    that session. Hiding Browser keeps its live guest mounted; stopping the session closes its tabs.
                </p>
            </section>

            <section>
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                        <h4 className="text-sm font-medium text-base-content">Replace Browser profile</h4>
                        <p className="mt-1 text-xs leading-relaxed text-muted">
                            Replace the isolated Browser profile with current Chrome or Edge cookies. Passwords, history, bookmarks, and autofill are never
                            imported.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn flex h-9 w-9 shrink-0 items-center justify-center border border-border text-muted hover:bg-base-200 hover:text-base-content"
                        aria-label="Refresh browser profiles"
                        disabled={busy !== null}
                        onClick={() => void loadProfiles()}
                    >
                        <RefreshCw size={14} className={busy === "profiles" ? "animate-spin" : ""} />
                    </button>
                </div>
                {cookieImportSupported === null || busy === "profiles" ? (
                    <p className="border border-border bg-base-200/40 p-3 text-xs text-muted">Looking for Chrome and Edge profiles…</p>
                ) : !cookieImportSupported ? (
                    <p className="border border-border bg-base-200/40 p-3 text-xs text-muted">
                        Install the latest WildLark desktop update to import website sessions.
                    </p>
                ) : profiles === null ? (
                    <p className="border border-error/30 bg-error/10 p-3 text-xs text-error">Browser profiles could not be read on this device.</p>
                ) : profiles.length === 0 ? (
                    <p className="border border-border bg-base-200/40 p-3 text-xs text-muted">No Chrome or Edge profiles with cookies were found.</p>
                ) : (
                    <div className="flex items-center gap-2 max-[600px]:flex-col max-[600px]:items-stretch">
                        <select
                            className="h-10 min-w-0 flex-1 border border-border bg-base-100 px-3 text-sm text-base-content"
                            aria-label="Browser profile"
                            value={selectedProfileId}
                            disabled={busy !== null}
                            onChange={(event) => setSelectedProfileId(event.currentTarget.value)}
                        >
                            {profiles.map((profile) => (
                                <option key={profile.id} value={profile.id}>
                                    {profile.name}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="btn h-10 shrink-0 bg-primary px-4 text-sm font-medium text-primary-content hover:bg-primary/80"
                            disabled={busy !== null || !selectedProfileId}
                            onClick={importCookies}
                        >
                            {busy === "import" ? "Replacing…" : "Replace and import"}
                        </button>
                    </div>
                )}
            </section>

            <section aria-disabled={!running} className={`border-t border-border pt-6 ${!running ? "opacity-50" : ""}`}>
                <h4 className="text-sm font-medium text-base-content">Active session</h4>
                <div className="mt-3 flex items-center justify-between gap-4 border border-border bg-base-200/40 p-3 max-[600px]:items-start">
                    <div>
                        <p className="text-sm font-medium text-base-content">Reset open tabs</p>
                        <p className="mt-1 text-xs text-muted">Close tabs and clear transient agent observations while keeping website sign-ins.</p>
                    </div>
                    <button
                        type="button"
                        className="btn h-9 shrink-0 border border-border bg-base-100 px-3 text-sm hover:bg-base-200"
                        disabled={!running || busy !== null}
                        onClick={resetTabs}
                    >
                        Reset tabs
                    </button>
                </div>
            </section>

            <section className="border-t border-border pt-6">
                <h4 className="text-sm font-medium text-error">Browser data</h4>
                <div className="mt-3 flex items-center justify-between gap-4 border border-error/30 bg-error/5 p-3 max-[600px]:items-start">
                    <div>
                        <p className="text-sm font-medium text-base-content">Delete browser data</p>
                        <p className="mt-1 text-xs text-muted">Delete cookies, cache, and local site storage from the isolated profile on this device.</p>
                    </div>
                    <button
                        type="button"
                        className="btn h-9 shrink-0 bg-error px-3 text-sm font-medium text-error-content hover:bg-error/90"
                        disabled={busy !== null}
                        onClick={deleteData}
                    >
                        {busy === "delete" ? "Deleting…" : "Delete data"}
                    </button>
                </div>
            </section>

            {message && (
                <p className="border border-border bg-base-200/40 px-3 py-2 text-xs text-muted" role="status">
                    {message}
                </p>
            )}
        </div>
    )
})
